/**
 * ReleaseBreakdownView — the release-rejected-units half of the merged
 * "Zarezerwuj wstępnie" dialog's pre-commit breakdown. Mirrors
 * lock-breakdown-view.tsx's structure for the release preview's own outcome
 * vocabulary (`released` / `nothing_to_release` / `unresolvable` —
 * ReleaseRejectedPreviewItemSchema), reusing the shared
 * OutcomeResultBucket/OutcomeResultEntryRow presentation.
 *
 * Traceability: client-offer-decision-persistence / Release-rejected-units
 * preview; offer-accept-reservation / Preliminary reservation action (the
 * dialog SHALL present the two halves as separately labelled groups).
 */
import { useTranslation } from 'react-i18next';
import type { BadgeProps } from '@ooh/ui/components/badge';
import type { ReleaseRejectedPreviewItem, ReleaseRejectedPreviewResponse } from '@ooh/contracts';
import {
  BreakdownGroupChip,
  BreakdownPreviewState,
  distinctUnitCount,
  OutcomeResultBucket,
  OutcomeResultEntryRow,
  type LooseT,
  type OutcomeResultUnitLookup,
} from '../../reservations/components/outcome-result-buckets';

const RELEASE_BREAKDOWN_OUTCOME_ORDER: ReleaseRejectedPreviewItem['outcome'][] = [
  'released',
  'nothing_to_release',
  'unresolvable',
];

const RELEASE_BREAKDOWN_LABEL_KEY: Record<ReleaseRejectedPreviewItem['outcome'], string> = {
  released: 'workspace.versions.unitDecisions.release.dialog.breakdown.releasedLabel',
  nothing_to_release:
    'workspace.versions.unitDecisions.release.dialog.breakdown.nothingToReleaseLabel',
  unresolvable: 'workspace.versions.unitDecisions.release.dialog.breakdown.unresolvableLabel',
};

const RELEASE_BREAKDOWN_BADGE_VARIANT: Record<
  ReleaseRejectedPreviewItem['outcome'],
  BadgeProps['variant']
> = {
  released: 'success',
  nothing_to_release: 'info',
  unresolvable: 'destructive',
};

interface ReleaseBreakdownGroup {
  outcome: ReleaseRejectedPreviewItem['outcome'];
  entries: ReleaseRejectedPreviewItem[];
}

function groupReleasePreviewItems(items: ReleaseRejectedPreviewResponse): ReleaseBreakdownGroup[] {
  return RELEASE_BREAKDOWN_OUTCOME_ORDER.map((outcome) => ({
    outcome,
    entries: items.filter((item) => item.outcome === outcome),
  })).filter((group) => group.entries.length > 0);
}

/**
 * This preview's `unresolvable` outcome is produced by exactly one server-
 * side case — a sold reservation — so the caller-language reason is a fixed
 * translated message, not a mapping over the raw `reason` string the server
 * sends (action-breakdown / "SHALL NOT print a raw error identifier").
 */
function releaseBreakdownUnresolvableDetail(t: LooseT): string {
  return t('workspace.versions.unitDecisions.release.dialog.breakdown.unresolvableSoldReason');
}

/** `nothing_to_release` is the one preview outcome with no window at all — a
 * REJECTED unit decision persists no periods, and `nothing_to_release`
 * means no reservation ever existed either, so there is nothing to show. */
function releaseEntryWindow(entry: ReleaseRejectedPreviewItem): { dateFrom?: Date; dateTo?: Date } {
  return entry.outcome === 'nothing_to_release'
    ? {}
    : { dateFrom: entry.dateFrom, dateTo: entry.dateTo };
}

export interface ReleaseBreakdownViewProps {
  preview: {
    isLoading: boolean;
    isError: boolean;
    data: ReleaseRejectedPreviewResponse | undefined;
    refetch: () => void;
  };
  lookup: OutcomeResultUnitLookup;
}

export function ReleaseBreakdownView({ preview, lookup }: ReleaseBreakdownViewProps) {
  const { t } = useTranslation('offers');
  const looseT = t as LooseT;
  const unknownUnitLabel = t('workspace.versions.unitDecisions.release.result.unknownUnit');

  const items = preview.data ?? [];
  const groups = groupReleasePreviewItems(items);

  return (
    <BreakdownPreviewState
      preview={preview}
      testIdPrefix="release-breakdown"
      loadingLabel={t('workspace.versions.unitDecisions.release.dialog.breakdown.loading')}
      errorLabel={t('workspace.versions.unitDecisions.release.dialog.breakdown.error')}
      retryLabel={t('workspace.versions.unitDecisions.release.dialog.breakdown.retryBtn')}
      isEmpty={items.length === 0}
      emptyLabel={t('workspace.versions.unitDecisions.release.dialog.breakdown.empty')}
    >
      <div className="space-y-2" data-testid="release-breakdown">
        <div className="flex flex-wrap gap-2" data-testid="release-breakdown-groups">
          {groups.map((group) => (
            <BreakdownGroupChip
              key={group.outcome}
              testId={`release-breakdown-group-${group.outcome}`}
              variant={RELEASE_BREAKDOWN_BADGE_VARIANT[group.outcome]}
              label={looseT(RELEASE_BREAKDOWN_LABEL_KEY[group.outcome])}
              count={distinctUnitCount(group.entries)}
            />
          ))}
        </div>

        {groups.map((group) => (
          <OutcomeResultBucket<ReleaseRejectedPreviewItem>
            key={group.outcome}
            trigger={
              <span className="text-sm font-medium">
                {looseT(RELEASE_BREAKDOWN_LABEL_KEY[group.outcome])}
              </span>
            }
            entries={group.entries}
            emptyLabel=""
            testIdPrefix="release-breakdown-entry"
            renderEntry={(entry) => (
              <OutcomeResultEntryRow
                unit={lookup.get(entry.mediaUnitId)}
                {...releaseEntryWindow(entry)}
                detail={
                  entry.outcome === 'unresolvable'
                    ? releaseBreakdownUnresolvableDetail(looseT)
                    : undefined
                }
                unknownUnitLabel={unknownUnitLabel}
              />
            )}
          />
        ))}
      </div>
    </BreakdownPreviewState>
  );
}
